// 帳號 VMS 裝置總覽（MV.4，docs/mycam/mv-spec.md ⑤）：每台攝影機的健康狀態 + 異常排序。
// 狀態照 K：ok | warn | fail | unknown（unknown 不告警）。
// 排序（K 沒做，我們做）：離線 > 儲存異常 / 空間不足 > 錄影停了 > 關了錄影 > 韌體有新版 > 正常。
// 資料：裝置清單（在線、錄影設定、韌體）+ 在線的攝影機再讀屬性（SD / 手機空間、正在錄影）。

export type Health = 'ok' | 'warn' | 'fail' | 'unknown';

/** 在線攝影機讀回來的屬性（讀不到的欄位不帶）。 */
export interface LiveAttrs {
  sdPresent?: boolean;
  /** 1 正常 / 2 異常 / 3 空間不足 / 4 格式化中 / 5 沒插卡 */
  sdStatus?: number;
  /** "Total=14GB,Used=0GB,Free=14GB" */
  sdCapacity?: string;
  recording?: boolean;
}

export interface OverviewInput {
  online: boolean;
  /** none / continuous / event / schedule…（裝置清單 recording.mode） */
  recordMode?: string;
  /** sdcard / cloud */
  recordLocation?: string;
  needsUpgrade?: boolean;
  /** 這台有沒有本機儲存（capability 0x8090） */
  hasStorage: boolean;
  /** 這台綁在有效的雲端方案裡（裝置清單 subscription.status = active） */
  cloudPlan?: boolean;
  attrs?: LiveAttrs;
}

export type IssueKey = 'offline' | 'storageFail' | 'noCard' | 'storageFull' | 'storageLow' | 'recStopped' | 'recOff' | 'firmware' | 'planUnused';

export interface OverviewRow {
  online: Health;
  storage: Health;
  recording: Health;
  firmware: Health;
  issues: IssueKey[];
  /** 0 最嚴重；排序用 */
  rank: number;
  capacity: Capacity | null;
  /** 沒插卡（含回報容量 0 / 0 GB）：錄到卡上才是問題，錄雲端只是中性說明 */
  noCard?: boolean;
}

export interface Capacity {
  totalGB: number;
  usedGB: number;
  freeGB: number;
}

export function parseCapacity(s?: string): Capacity | null {
  if (!s) return null;
  const n = (k: string) => {
    const m = new RegExp(`${k}=([\\d.]+)\\s*GB`, 'i').exec(s);
    return m ? Number(m[1]) : NaN;
  };
  const c = { totalGB: n('Total'), usedGB: n('Used'), freeGB: n('Free') };
  return Number.isFinite(c.totalGB) && Number.isFinite(c.freeGB) ? { ...c, usedGB: Number.isFinite(c.usedGB) ? c.usedGB : c.totalGB - c.freeGB } : null;
}

const LOW_FREE_FRACTION = 0.05;

export function overviewOf(d: OverviewInput): OverviewRow {
  const issues: IssueKey[] = [];
  const a = d.attrs ?? {};
  const recMode = d.recordMode || 'none';
  const recordingOn = recMode !== 'none';
  const toSd = recordingOn && d.recordLocation === 'sdcard';
  const cap0 = parseCapacity(a.sdCapacity);
  // 回報容量 0 / 0 GB = 沒插卡（615 實例：以前畫成綠色「剩 0 / 0 GB」）
  const zeroCard = !!cap0 && cap0.totalGB <= 0;
  const capacity = zeroCard ? null : cap0;

  if (!d.online) {
    issues.push('offline');
    // 離線：其他欄位不知道（不告警），韌體照清單
    const firmware: Health = d.needsUpgrade ? 'warn' : 'ok';
    if (d.needsUpgrade) issues.push('firmware');
    return { online: 'fail', storage: 'unknown', recording: 'unknown', firmware, issues, rank: 0, capacity: null };
  }

  let storage: Health = 'unknown';
  let noCard = false;
  if (d.hasStorage) {
    if (a.sdStatus === 2) {
      storage = 'fail';
      issues.push('storageFail');
    } else if (a.sdStatus === 5 || a.sdPresent === false || zeroCard) {
      // 沒插卡：要錄到卡上才算問題；錄雲端 → 灰色中性
      storage = toSd ? 'fail' : 'unknown';
      noCard = true;
      if (toSd) issues.push('noCard');
    } else if (a.sdStatus === 3) {
      storage = 'warn';
      issues.push('storageFull');
    } else if (capacity && capacity.totalGB > 0 && capacity.freeGB / capacity.totalGB < LOW_FREE_FRACTION) {
      storage = 'warn';
      issues.push('storageLow');
    } else if (a.sdStatus !== undefined || a.sdPresent !== undefined) {
      storage = 'ok';
    }
  }

  let recording: Health = 'unknown';
  if (!recordingOn) {
    recording = 'warn'; // 關了錄影：使用者自己選的，但總覽要看得到（spec 驗收「關錄影」排上面）
    issues.push('recOff');
  }
  else if (a.recording === undefined) recording = 'unknown';
  else if (recMode === 'continuous' && a.recording === false) {
    recording = 'fail';
    issues.push('recStopped');
  } else recording = 'ok'; // 事件錄影沒事件時不在錄 = 正常

  // 佔了雲端方案名額卻錄到 SD 卡：名額白佔（別台選雲端會被「方案已滿」擋）
  const planUnused = !!d.cloudPlan && d.recordLocation === 'sdcard';
  if (planUnused) issues.push('planUnused');

  const firmware: Health = d.needsUpgrade ? 'warn' : 'ok';
  if (d.needsUpgrade) issues.push('firmware');

  const rank = storage === 'fail' || storage === 'warn' ? 1 : recording === 'fail' ? 2 : recording === 'warn' ? 3 : firmware === 'warn' || planUnused ? 3.5 : 4;
  return { online: 'ok', storage, recording, firmware, issues, rank, capacity, noCard };
}

/** 異常排序：rank 小的在前；同 rank 依名稱。 */
export function sortRows<T extends { row: OverviewRow; name: string }>(xs: T[]): T[] {
  return [...xs].sort((a, b) => a.row.rank - b.row.rank || a.name.localeCompare(b.name));
}
